import BaseApiService from './BaseApiService'
import type { ApprovalLevel, PromotionApprovalRule, PromotionRequest, PromotionRequestStatus, Template, TemplateVersion, TemplateVariable } from '../types'
import type { CanonicalDocument } from '../cdm/types'

export interface DriftEntry { rawName: string; normalizedKey: string; kind: string }
export interface DriftChange { rawName: string; normalizedKey: string; oldKind: string; newKind: string }
export interface SchemaDrift { added: DriftEntry[]; removed: DriftEntry[]; changed: DriftChange[] }

export interface UploadTemplateResponse {
  templateVersionId: string
  versionNumber: number
  variables: TemplateVariable[]
  /** Per-tag log of a vendor conversion; empty unless sourceVendor was supplied. */
  conversionChanges: string[]
  drift?: SchemaDrift | null
}

export interface CreateTemplateResponse {
  templateId: string
}

export interface PromoteOptions {
  changeNote?: string
  /** ISO 8601 with offset; omit/null to promote immediately. */
  promoteAt?: string | null
  concurrencyToken?: string | null
}

export type PromoteResponse =
  | { promoted: true; scheduled?: false; versionNumber: number; previousLiveVersionId: string | null }
  | { scheduled: true; promoted?: false; scheduledPromotionId: string; promoteAt: string; versionNumber: number }
  /** Level self/dual: a request was raised instead. promoteAt applies once approved. */
  | { approvalRequired: true; approvalLevel: Exclude<ApprovalLevel, 'auto'>; promoted?: false; scheduled?: false; promotionRequestId: string; promoteAt: string | null; versionNumber: number }

export interface ApproveResponse {
  approved: true
  promoted?: true
  scheduled?: true
  promoteAt?: string
  versionNumber: number
}

export interface MoveBulkResponse {
  moved: number
  skipped: { unauthorized: string[]; notFound: string[] }
}

class TemplatesApiService extends BaseApiService {
  create(payload: { name: string; description?: string; folderId?: string }): Promise<CreateTemplateResponse> {
    return this.post<CreateTemplateResponse>('/v1/templates/create', payload)
  }

  /**
   * `sourceVendor` ('carbone' | 'docmosis' | 'handlebars') rewrites the vendor tags server-side
   * before parsing and archives the untouched upload as original.docx.
   */
  upload(
    file: File,
    templateId: string,
    changeNote?: string,
    sourceVendor?: string,
    onProgress?: (pct: number) => void
  ): Promise<UploadTemplateResponse> {
    const form = new FormData()
    form.append('file', file)
    if (changeNote) form.append('changeNote', changeNote)
    if (sourceVendor) form.append('sourceVendor', sourceVendor)
    return this.postFormWithProgress<UploadTemplateResponse>(
      `/v1/templates/${templateId}/upload`, form, onProgress)
  }

  uploadText(templateId: string, sourceText: string, format: 'html' | 'md', changeNote?: string): Promise<UploadTemplateResponse> {
    return this.post<UploadTemplateResponse>(`/v1/templates/${templateId}/upload-text`, { sourceText, format, changeNote })
  }

  getById(templateId: string): Promise<Template> {
    return this.post<Template>('/v1/templates/get', { templateId })
  }

  list(folderId?: string, opts?: {
    name?: string
    statusLookupId?: number
    /** Ignores folderId entirely and returns every template in the workspace. Not recursive. */
    includeAllFolders?: boolean
    page?: number
    pageSize?: number
  }): Promise<Template[]> {
    return this.post<Template[]>('/v1/templates/list', { folderId, ...opts })
  }

  /**
   * Every template in the workspace, paged through server-side (the API caps pageSize at 200).
   * The folder tree needs the whole set to show per-folder file counts and leaf rows.
   */
  async listEverything(pageSize = 200, maxPages = 25): Promise<Template[]> {
    const all: Template[] = []
    for (let page = 1; page <= maxPages; page++) {
      const batch = await this.list(undefined, { includeAllFolders: true, page, pageSize })
      all.push(...batch)
      if (batch.length < pageSize) break
    }
    return all
  }

  /** Name, description and folder are all written, so pass the current values of the ones not changing. 409 on a stale token. */
  update(template: Pick<Template, 'templateId' | 'description' | 'folderId' | 'concurrencyToken'>, name: string): Promise<{ updated: boolean }> {
    return this.post<{ updated: boolean }>('/v1/templates/update', {
      templateId: template.templateId,
      name,
      description: template.description,
      folderId: template.folderId,
      concurrencyToken: template.concurrencyToken,
    })
  }

  /** Partial success: ids the caller may not move come back under `skipped`. */
  moveBulk(templateIds: string[], targetFolderId?: string | null): Promise<MoveBulkResponse> {
    return this.post<MoveBulkResponse>('/v1/templates/move-bulk', {
      templateIds,
      targetFolderId: targetFolderId ?? null,
    })
  }

  listVersions(templateId: string): Promise<TemplateVersion[]> {
    return this.post<TemplateVersion[]>('/v1/templates/list-versions', { templateId })
  }

  getVariables(templateVersionId: string): Promise<TemplateVariable[]> {
    return this.post<TemplateVariable[]>('/v1/templates/variables', { templateVersionId })
  }

  delete(templateId: string): Promise<void> {
    return this.post<void>('/v1/templates/delete', { templateId })
  }

  /**
   * Promotes a version to Live, replacing whatever version is live now. Only one version per
   * template is ever live. `promoteAt` (ISO with offset) schedules it instead of applying now.
   * 400 CHANGE_NOTE_REQUIRED when the team requires a note; 409 on a stale concurrencyToken.
   */
  promote(templateVersionId: string, opts: PromoteOptions = {}): Promise<PromoteResponse> {
    return this.post<PromoteResponse>('/v1/templates/promote', {
      templateVersionId,
      changeNote: opts.changeNote || undefined,
      promoteAt: opts.promoteAt ?? null,
      concurrencyToken: opts.concurrencyToken ?? undefined,
    })
  }

  listPromotionRequests(filter: { status?: PromotionRequestStatus; templateId?: string } = {}): Promise<PromotionRequest[]> {
    return this.post<PromotionRequest[]>('/v1/templates/promotion-requests/list', filter)
  }

  /** 403 without MANAGE, or for the requester at level dual; 409 if already decided or the version is gone. */
  approvePromotionRequest(promotionRequestId: string, note?: string): Promise<ApproveResponse> {
    return this.post<ApproveResponse>('/v1/templates/promotion-requests/approve', { promotionRequestId, note: note || undefined })
  }

  rejectPromotionRequest(promotionRequestId: string, note?: string): Promise<{ rejected: true }> {
    return this.post('/v1/templates/promotion-requests/reject', { promotionRequestId, note: note || undefined })
  }

  cancelPromotionRequest(promotionRequestId: string, note?: string): Promise<{ cancelled: true }> {
    return this.post('/v1/templates/promotion-requests/cancel', { promotionRequestId, note: note || undefined })
  }

  /** Team admins. null inherits from the folder chain / team default. */
  setPromotionApproval(templateId: string, level: ApprovalLevel | null): Promise<Omit<PromotionApprovalRule, 'dualAvailable' | 'dualUnavailableReason'>> {
    return this.post('/v1/templates/set-promotion-approval', { templateId, level })
  }

  cancelScheduledPromotion(templateId: string): Promise<{ cancelled: boolean }> {
    return this.post('/v1/templates/cancel-scheduled-promotion', { templateId })
  }

  /** Takes the template out of Live entirely; no version is live afterwards. */
  demote(templateId: string, changeNote?: string): Promise<{ demoted: boolean; demotedVersionId?: string }> {
    return this.post('/v1/templates/demote', { templateId, changeNote: changeNote || undefined })
  }

  /** Creates a brand-new template whose v1 is a copy of this version. Not in Live. */
  copyVersion(
    templateVersionId: string,
    opts: { name?: string; folderId?: string | null; description?: string } = {},
  ): Promise<{ templateId: string; templateVersionId: string; name: string }> {
    return this.post('/v1/templates/copy-version', { templateVersionId, ...opts })
  }

  getPersistence(templateId: string): Promise<{ templateNoStore: boolean; teamNoStore: boolean; effectiveNoStore: boolean }> {
    return this.post('/v1/templates/persistence/get', { templateId })
  }

  setPersistence(templateId: string, noStore: boolean): Promise<{ templateNoStore: boolean; teamNoStore: boolean; effectiveNoStore: boolean }> {
    return this.post('/v1/templates/persistence', { templateId, noStore })
  }

  setRetention(templateId: string, retentionDays: number | null): Promise<{ retentionDays: number | null }> {
    return this.post('/v1/templates/retention', { templateId, retentionDays })
  }

  setLegalHold(templateId: string, legalHold: boolean): Promise<{ legalHold: boolean }> {
    return this.post('/v1/templates/set-legal-hold', { templateId, legalHold })
  }

  setStorageRegion(templateId: string, region: string | null): Promise<{ storageRegion: string | null }> {
    return this.post('/v1/templates/set-storage-region', { templateId, region })
  }

  // ── WYSIWYG editor backend (Part 1.4 of the plan) ─────────────────────────

  /** Fetches a template version's canonical document, ungzipped, for the editor to open. */
  getCanonical(templateVersionId: string): Promise<{ templateVersionId: string; versionNumber: number; canonicalDocument: CanonicalDocument }> {
    return this.post('/v1/templates/canonical', { templateVersionId })
  }

  /**
   * Saves the editor's document as a new version. `baseTemplateVersionId` is the version the
   * editor opened — a 409 (ApiError with code CONCURRENCY_CONFLICT) means someone else saved
   * underneath the editor since it opened; a 422 (ApiError.detail = { clashErrors }) means a
   * moniker clash the server's re-discovery found. The new version is not live until it is
   * promoted (see promote).
   */
  saveCanonical(
    templateId: string,
    baseTemplateVersionId: string | null,
    canonicalDocument: CanonicalDocument,
    changeNote?: string,
    isDraft = true
  ): Promise<{ templateVersionId: string; versionNumber: number; variables: TemplateVariable[] }> {
    return this.post('/v1/templates/save-canonical', {
      templateId,
      baseTemplateVersionId,
      canonicalJson: JSON.stringify(canonicalDocument),
      changeNote,
      isDraft,
    })
  }

  /** Fetches one image asset's bytes (a resolved `ImageBlock.blobRef`) for display in the editor. */
  getAsset(templateVersionId: string, blobRef: string): Promise<Blob> {
    return this.postBlob('/v1/templates/asset', { templateVersionId, blobRef })
  }

  /** Fetches an image staged for a template that has no saved version yet (an upload, or a team block's image). */
  getStagedAsset(templateId: string, blobRef: string): Promise<Blob> {
    return this.postBlob('/v1/templates/asset', { templateId, blobRef })
  }

  /** Uploads a newly-inserted editor image ahead of the save that will reference it. */
  uploadAsset(templateId: string, file: File): Promise<{ blobRef: string }> {
    const form = new FormData()
    form.append('templateId', templateId)
    form.append('file', file)
    return this.postForm('/v1/templates/upload-asset', form)
  }

  // FR-3.2.3: schema drift for a version vs its predecessor
  schemaDrift(templateVersionId: string): Promise<{
    templateVersionId: string
    priorVersionId: string | null
    priorVersionNumber: number | null
    hasDrift: boolean
    added: DriftEntry[]
    removed: DriftEntry[]
    changed: DriftChange[]
  }> {
    return this.post('/v1/templates/schema-drift', { templateVersionId })
  }
}

export const templatesApiService = new TemplatesApiService()
